import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Place : famille "lieu physique identifié" (interne ou externe).
 *
 * Lieu concret repéré par l'organisation : site, salle, réserve, zone de
 * stockage, chantier, domicile ou véhicule. Construite sur le modèle
 * "famille + type" de la bible (patron : `asset`). Le socle ne connaît PAS de
 * lieu concret ("salle 12", "réserve du fond") : il connaît la famille
 * générique `place` ; le vocabulaire métier (le `kind` de la fiche) est porté
 * par le champ `type` (chaîne libre déclarée par l'app : "site", "salle",
 * "reserve", "chantier", "domicile", "vehicule").
 *
 * Un lieu peut s'emboîter dans un autre (`parentPlaceId`, une salle dans un
 * site), appartenir à un tiers (`ownerContactId`) exploité sous mandat de
 * gestion (`managingAgreementId`), et s'adosser à un bien (`assetId`, ex. un
 * véhicule). Les resources, stocks, ordres de travail et créneaux pointent VERS
 * le lieu (référence portée par ces familles), le lieu ne les stocke donc pas.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 *
 * Convention de patron (identique à asset) :
 *   - `placeCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `PlaceSchema`            : famille complète = champs standard + métier.
 *   - `PlaceCreateInputSchema` : ce que l'appelant fournit à la création
 *     (champs métier ; les champs standard sont remplis par le SDK). Sert à
 *     VALIDER l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille place (hors champs standard du socle). */
export const placeCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (le `kind` de la fiche : "site", "salle", "chantier", "vehicule"...). */
  type: z.string().min(1),
  /** Adresse postale ou libellé de localisation, texte libre, optionnel. */
  address: z.string().min(1).optional(),
  /** Lieu parent dans lequel ce lieu s'emboîte (référence `place`), optionnel. */
  parentPlaceId: z.string().min(1).optional(),
  /** Le lieu appartient-il à l'organisation (interne) plutôt qu'à un tiers (externe), optionnel. */
  isInternal: z.boolean().optional(),
  /** Coordonnées géographiques (ex. { lat, lng }), objet structuré, optionnel. */
  geo: z.record(z.string(), z.unknown()).optional(),
  /** Fiche `contact` du propriétaire si le lieu appartient à un tiers, optionnel. */
  ownerContactId: z.string().min(1).optional(),
  /** Mandat de gestion sous lequel le lieu est exploité (référence `agreement`), optionnel. */
  managingAgreementId: z.string().min(1).optional(),
  /** Bien matériel adossé au lieu (référence `asset`, ex. un véhicule), optionnel. */
  assetId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille place (champs standard + champs métier). */
export const PlaceSchema = z.object({
  ...baseEntityFields,
  ...placeCoreFields,
});

/** Type d'un place. */
export type Place = z.infer<typeof PlaceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const PlaceCreateInputSchema = z.object({
  ...placeCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un place. */
export type PlaceCreateInput = z.infer<typeof PlaceCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const PlaceUpdateInputSchema = z
  .object({ ...placeCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un place. */
export type PlaceUpdateInput = z.infer<typeof PlaceUpdateInputSchema>;
